import React, { useEffect, useState } from 'react';
import { assistantAPI } from '../../services/assistant';
import useAssistant from '../../store/useAssistant';
import AssistantUsage from './AssistantUsage';
import AssistantModels from './AssistantModels';
import AssistantGlossary from './AssistantGlossary';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
const LEVELS = [
  { value: 'full', label: 'Full' },
  { value: 'totals', label: 'Totals only' },
  { value: 'none', label: 'Not available' },
];

/**
 * Company admins: turn the assistant on, see its use and estimated cost, and choose
 * what it may read from each dataset.
 */
export default function AssistantSettings({ companyId, companyName }) {
  const setStatus = useAssistant((s) => s.setStatus);
  const [status, setLocalStatus] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, d] = await Promise.all([assistantAPI.status(companyId), assistantAPI.datasets(companyId)]);
      setLocalStatus(s.data);
      setStatus(s.data);
      setDatasets(d.data || []);
      setError('');
    } catch (err) {
      setError(describeError(err, 'Could not read the assistant settings'));
    }
  };

  useEffect(() => {
    if (companyId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const toggle = async (enabled) => {
    setSaving(true);
    try {
      await assistantAPI.setEnabled(companyId, enabled);
      await load();
    } catch (err) {
      setError(describeError(err, 'Could not change the setting'));
    } finally {
      setSaving(false);
    }
  };

  const setAccess = async (dataset, access) => {
    setSaving(true);
    try {
      await assistantAPI.setDatasetAccess(companyId, dataset, access);
      await load();
    } catch (err) {
      setError(describeError(err, 'Could not change the setting'));
    } finally {
      setSaving(false);
    }
  };

  if (!status) return error ? <div className="error-message" role="alert">{error}</div> : null;

  return (
    <section className="settings-section" aria-labelledby="assistant-heading">
      <h3 id="assistant-heading">AI assistant</h3>
      {!status.configured && <p className="settings-help">The AI assistant is not set up on this server yet.</p>}
      <label className="settings-toggle">
        <input type="checkbox" checked={!!status.enabled} disabled={saving || !status.configured} onChange={(e) => toggle(e.target.checked)} />
        <span>
          <strong>Turn on the AI assistant for {companyName}</strong>
          <small>
            People can ask questions about the company&apos;s data and have reports built for them. To answer, the AI assistant sends the question,
            descriptions of the datasets and the results of the queries it runs to OpenAI. OpenAI does not use them to train its models, and may keep
            them for up to 30 days to monitor misuse. Nothing is saved or changed in the wallboard without the person&apos;s confirmation.
          </small>
        </span>
      </label>
      {status.canManage && status.configured && <AssistantModels companyId={companyId} status={status} onSaved={load} />}
      {status.canManage && <AssistantGlossary companyId={companyId} />}
      {status.canManage && <AssistantUsage companyId={companyId} />}

      {datasets.length > 0 && (
        <>
          <h4>What the AI assistant may read</h4>
          <p className="settings-help">
            Totals only: the AI assistant can count and sum, but never lists rows, never groups or filters by a person, and hides groups too small to keep people anonymous.
          </p>
          <div className="datasets-table-container">
            <table className="datasets-table">
              <thead><tr><th>Dataset</th><th>Assistant access</th></tr></thead>
              <tbody>
                {datasets.map((d) => (
                  <tr key={d.dataset}>
                    <td><code>{d.dataset}</code>{d.personalColumns.length > 0 && <div className="kpi-key">Personal: {d.personalColumns.join(', ')}</div>}</td>
                    <td>
                      <select aria-label={`AI assistant access to ${d.dataset}`} value={d.setHere ? d.access : ''} disabled={saving} onChange={(e) => setAccess(d.dataset, e.target.value || null)}>
                        <option value="">Default ({LEVELS.find((l) => l.value === d.defaultAccess)?.label.toLowerCase()})</option>
                        {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {error && <div className="error-message" role="alert">{error}</div>}
    </section>
  );
}
